import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { uploadMedableFile } from '../../file_upload';
const COMPLETE_PTS_DOCUMENT_ROUTE = '/v2/routes/econsent/complete_pts_document';
/** Formats a `PlainDate` as a timezone-free `YYYY-MM-DD` wallclock string. */
export const formatWallclockDate = (date) => date.toString();
/**
 * Resolves the blank-PDF download URLs by reading the signed document with
 * `expand` on `ec__published_studio_template`.  The participant documents list
 * API does not expand nested references, so File `url` facets and Reference
 * expansions are only available from a single-object read.
 *
 * NOTE: the `paths` param is intentionally omitted — Cortex cannot resolve
 * dotted paths that traverse into expanded references (e.g.
 * `ec__published_studio_template.ec__bundle`), which causes a 404.
 */
export const usePtsBlankPdfUrls = ({ apiClient, signedDocId, accountId, enabled }) => {
    const query = useQuery({
        queryKey: ['pts-blank-pdf-urls', signedDocId, accountId],
        enabled: enabled && !!apiClient && !!accountId && !!signedDocId,
        queryFn: async () => {
            if (!apiClient || !accountId) {
                return null;
            }
            const response = await apiClient.get(`/v2/account/${accountId}/ec__site_documents/${signedDocId}`, {
                params: {
                    expand: ['ec__published_studio_template'],
                },
            });
            const data = response?.data;
            return {
                blankPdfUrl: data?.ec__document_template?.ec__pts_document?.url ?? null,
                bundleUrl: data?.ec__published_studio_template?.ec__bundle?.url ?? null,
            };
        },
    });
    return {
        blankPdfUrl: query.data?.blankPdfUrl ?? undefined,
        bundleUrl: query.data?.bundleUrl ?? undefined,
        isResolvingBlankPdf: query.isLoading,
    };
};
/**
 * Resolves the `ec__final_document` download URL by reading the signed document
 * fresh by id. Medable File `url` facets resolve on single-object reads, but the
 * participant documents *list* read does not reliably populate them, so the
 * completed-view fetches directly to guarantee a working download link.
 */
export const usePtsFinalDocumentUrl = ({ apiClient, signedDocId, accountId, enabled }) => {
    const query = useQuery({
        queryKey: ['pts-final-document-url', signedDocId, accountId],
        enabled: enabled && !!apiClient && !!accountId && !!signedDocId,
        queryFn: async () => {
            if (!apiClient || !accountId) {
                return null;
            }
            const response = await apiClient.get(`/v2/account/${accountId}/ec__site_documents/${signedDocId}`, { params: { paths: ['ec__final_document'] } });
            return response?.data?.ec__final_document?.url ?? null;
        },
    });
    return { finalDocumentUrl: query.data ?? undefined, isResolving: query.isLoading };
};
/**
 * Wraps the legacy PTS completion sequence on the modern apiClient:
 *   1. If a signing date is provided, POST `complete_pts_document` with
 *      `ec__completion_time === ec__pts_signature_date_wallclock`.
 *   2. If a file is provided, upload it to `ec__final_document` via the
 *      standalone Medable upload primitive.
 */
export const usePtsDocumentModal = ({ apiClient, signedDocId, accountId, onComplete }) => {
    // The completion POST marks the document `complete` on the backend. If the
    // subsequent file upload fails the user can retry; we must not re-post the
    // same completion. Track the last committed signing date (not just a boolean)
    // so a retry with the same date is skipped, but a corrected date before retry
    // is re-posted instead of being silently dropped.
    const lastCompletedWallclockRef = useRef(undefined);
    const mutation = useMutation({
        mutationFn: async ({ signatureDate, file, reasonForChange }) => {
            if (!apiClient) {
                throw new Error('An API client is required to complete a PTS document.');
            }
            if (signatureDate) {
                const wallclockDate = formatWallclockDate(signatureDate);
                if (wallclockDate !== lastCompletedWallclockRef.current) {
                    await apiClient.post(COMPLETE_PTS_DOCUMENT_ROUTE, {
                        ec__completion_time: wallclockDate,
                        ec__signed_document: signedDocId,
                        ec__pts_signature_date_wallclock: wallclockDate,
                        ...(reasonForChange ? { ec__reason_for_change: reasonForChange } : {}),
                    });
                    lastCompletedWallclockRef.current = wallclockDate;
                }
            }
            if (file) {
                if (!accountId) {
                    throw new Error('A site user account is required to upload a document.');
                }
                await uploadMedableFile({ apiClient, accountId, signedDocId, file });
            }
        },
        onSuccess: () => onComplete?.(),
    });
    return {
        submitPtsDocument: mutation.mutateAsync,
        isSubmitting: mutation.isPending,
        submitError: mutation.error,
    };
};
